import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, ChevronLeft, ChevronRight, LogOut, ArrowUpCircle } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { useCurrentUser } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useNavItems, type NavGroup } from '../../hooks/useNavItems';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';

interface SidebarItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: number;
  isCollapsed: boolean;
  isLocked?: boolean;
  onLockedClick?: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ to, icon: Icon, label, badge, isCollapsed, isLocked, onLockedClick }) => {
  const location = useLocation();
  
  const isActive = location.pathname === to;
  const targetTo = to;

  const handleClick = (e: React.MouseEvent) => {
    if (isLocked && onLockedClick) {
      e.preventDefault();
      onLockedClick();
    }
  };

  return (
    <Link
      to={targetTo}
      onClick={handleClick}
      title={isCollapsed ? label : undefined}
      className={cn(
        'flex items-center gap-4 px-4 py-3 border-2 border-transparent text-sm transition-all relative group uppercase font-bold tracking-widest font-mono',
        isActive 
          ? 'bg-[#0044FF] text-white border-black shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:border-white translate-x-1' 
          : 'text-gray-700 hover:bg-black hover:text-white hover:border-black hover:translate-x-1 dark:text-gray-300 dark:hover:bg-white dark:hover:text-black dark:hover:border-white',
        isLocked && 'opacity-60'
      )}
    >
      <Icon size={18} className="flex-shrink-0" />
      {!isCollapsed && <span className="truncate">{label}</span>}
      {isLocked && !isCollapsed && (
        <Lock size={14} className="ml-auto text-current opacity-60" />
      )}
      {typeof badge === 'number' && badge > 0 && !isLocked && (
        <Badge 
          className={cn(
            'px-2 py-0.5 min-w-[20px] justify-center bg-[#FFD600] text-black border-2 border-black rounded-none text-[10px]',
            isCollapsed ? 'absolute -top-2 -right-2' : 'ml-auto'
          )}
        >
          {badge}
        </Badge>
      )}
    </Link>
  );
};

// Section header accent mirrors the group's semantic colour.
const GROUP_ACCENT: Record<NavGroup['accent'], string> = {
  blue: 'border-[#0044FF]',
  yellow: 'border-[#FFD600]',
  black: 'border-black dark:border-white',
};

export const Sidebar = () => {
  const { node, isLoading: isTenantLoading } = useTenant();
  const { signOut, isLoading: isUserLoading } = useCurrentUser();
  const { role, isAdmin } = usePermissions();
  const { groups } = useNavItems();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const navigate = useNavigate();

  const isLoading = isTenantLoading || isUserLoading;

  if (isLoading) {
    // Skeleton item counts approximate a typical MEMBER layout to minimize CLS
    const skeletonGroups = [2, 5, 3];
    return (
      <aside 
        className={cn(
          'hidden md:flex flex-col bg-[#F5F5F0] border-r-8 border-black py-6 transition-all duration-300 relative z-30 overflow-visible dark:bg-black dark:border-white',
          isCollapsed ? 'w-20' : 'w-20 md:w-20 lg:w-72'
        )}
      >
        <div className="flex-1 px-4 space-y-8">
          {skeletonGroups.map((count, group) => (
            <div key={group} className="space-y-3">
              <div className="h-4 bg-gray-200 dark:bg-white/10 rounded-none w-16 animate-pulse hidden lg:block" />
              {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="h-12 bg-white dark:bg-white/5 border-2 border-black dark:border-white dark:border-white/10 rounded-none w-full animate-pulse" />
              ))}
            </div>
          ))}
        </div>
      </aside>
    );
  }
  
  return (
    <aside 
      className={cn(
        'hidden md:flex flex-col bg-[#F5F5F0] border-r-8 border-black py-6 transition-all duration-300 relative z-30 overflow-visible dark:bg-black dark:border-white',
        isCollapsed ? 'w-20' : 'w-20 md:w-20 lg:w-72'
      )}
    >
      <button 
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-5 top-6 w-10 h-10 bg-black text-white border-4 border-black !rounded-none flex items-center justify-center hover:bg-[#FFD600] hover:text-black hover:shadow-[4px_4px_0px_#000000] z-40 hidden lg:flex !p-0 transition-all dark:bg-white dark:text-black dark:border-white dark:hover:bg-[#FFD600] dark:hover:shadow-[4px_4px_0px_#FFFFFF]"
      >
        {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
      </button>

      <div className="flex-1 overflow-y-auto scrollbar-hide px-4 space-y-8">
        {groups.map((group) => (
          <div key={group.id}>
            {!isCollapsed && (
              <p className={cn(
                'text-xs font-black uppercase tracking-[0.2em] text-black dark:text-white mb-4 px-2 font-mono hidden lg:block border-l-4 pl-2',
                GROUP_ACCENT[group.accent]
              )}>
                {group.label}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((item) => (
                <SidebarItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  badge={item.badge}
                  isCollapsed={isCollapsed}
                  isLocked={item.locked}
                  onLockedClick={() => setShowUpgradeModal(true)}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="mt-8 pt-6 border-t-8 border-black dark:border-white space-y-4">


          <button
            onClick={() => signOut()}
            className={cn(
              'flex items-center gap-4 px-4 py-3 border-2 border-red-600 text-sm transition-all w-full text-red-600 bg-white hover:bg-red-600 hover:text-white dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white font-bold font-mono uppercase tracking-widest',
              isCollapsed ? 'justify-center' : ''
            )}
            title={isCollapsed ? "Sign Out" : undefined}
          >
            <LogOut size={18} className="flex-shrink-0" />
            {!isCollapsed && <span className="truncate lg:block hidden">Sign Out</span>}
          </button>

          <div className={cn(
            'flex items-center gap-3 px-4 py-3 border-2 border-black bg-black text-white dark:bg-white dark:text-black dark:border-white',
            isCollapsed ? 'justify-center' : ''
          )}>
            <div className="w-3 h-3 bg-[#0044FF] border-2 border-white" />
            {!isCollapsed && (
              <span className="text-xs font-bold uppercase tracking-widest font-mono truncate hidden lg:block">
                {role}
              </span>
            )}
          </div>
        </div>
        <div className="h-6"></div>
      </div>

      {/* Tier Upgrade Modal */}
      <Modal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade Your Plan"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowUpgradeModal(false)}>
              Maybe Later
            </Button>
            {isAdmin && (
              <Button variant="primary" onClick={() => { setShowUpgradeModal(false); navigate('/settings/billing'); }}>
                View Plans
              </Button>
            )}
          </>
        }
      >
        <div className="text-center">
          <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-none flex items-center justify-center mx-auto mb-4 dark:bg-amber-500/10">
            <ArrowUpCircle size={28} />
          </div>
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
            This feature requires a higher-tier plan than your organization's current{' '}
            <span className="font-semibold text-gray-800 dark:text-slate-200">{node?.tier || 'LOCAL'}</span> subscription.
          </p>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {isAdmin
              ? 'Upgrade your plan to unlock this and other premium capabilities.'
              : 'Contact your administrator to discuss upgrading your organization.'}
          </p>
        </div>
      </Modal>
    </aside>
  );
};
